import { doc, setDoc, Timestamp, increment, type Firestore } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { useUsersStore } from '@/stores/users'
import { useTenantsStore } from '@/stores/tenants'
import { useProjectsStore } from '@/stores/projects'
import { logger } from '@/utils/logger'
import { localParts } from '@/utils/activityTime'
import { CsmAction } from '@/constants/csmActions'

const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000 // 10 min < 30-min bucket → no skipped buckets

// Optional per-action project metadata, merged into labels.projects[projectId] (per-day snapshot).
export interface ProjectActivityMeta {
  norm_count?: number
  directive_count?: number // active task directives on this project (per-day snapshot)
  // MYAC-859 — per-event discriminators, grouped under `action_meta` so they never
  // clobber the identity `role` label. Last-write-wins within the day.
  role?: string // project_member_role_changed: the member's new role
  count?: number // task_accepted / task_rejected (bulk): tasks reviewed in one action
  kind?: string // audit_ai_requested: grid | audit_ai | ccapa | strengths
  assigned?: boolean // task_assigned: false = assignee cleared
  status?: string // task_status_changed: the new review_progress
}

// The per-event discriminator subset of ProjectActivityMeta — everything except
// the flat count fields — persisted under labels.projects[id].action_meta.
type ActionMetaKey = keyof Omit<ProjectActivityMeta, 'norm_count' | 'directive_count'>
// Keyed by ActionMetaKey so the object literal MUST list every such field: add a
// new discriminator to the interface above and this fails to compile until it's
// added here too (no silent drift).
const ACTION_META_KEY_SET: Record<ActionMetaKey, true> = {
  role: true,
  count: true,
  kind: true,
  assigned: true,
  status: true,
}
const ACTION_META_KEYS = Object.keys(ACTION_META_KEY_SET) as ActionMetaKey[]

/**
 * MYAC-1138 — discriminated counters. An action in this set writes its leaf as a
 * MAP of bucket → count instead of a bare counter:
 *
 *   actions[tenant][project][referential_adopted]['iso13485:stage2'] = increment(1)
 *
 * HARD RULE: only actions that have NEVER shipped as a bare counter may join this
 * set. Changing a live counter's shape breaks hand-written SQL against the Supabase
 * mirror, which reads the leaf as an integer.
 */
const BUCKETED_ACTIONS: ReadonlySet<string> = new Set<string>([
  CsmAction.ReferentialAdopted,
  CsmAction.ReferentialRemoved,
  CsmAction.BillingPlanSwitchStarted,
])

/**
 * Firestore map keys may not contain `/` and may not start or end with `__`.
 * Referential ids come from an authored catalogue, so they are constrained here
 * rather than trusted.
 */
export function sanitizeBucket(raw: string): string {
  // Truncate BEFORE trimming leading/trailing `_`: cutting at 64 chars can land
  // inside an internal underscore run and leave a trailing `__` behind — which
  // Firestore rejects as a map key just like a raw one would. Trimming after
  // truncation removes exactly that reintroduced run (and any the cut exposed
  // at the front too).
  const cleaned = raw
    .replace(/[^A-Za-z0-9_.:-]/g, '_')
    .slice(0, 64)
    .replace(/^_+|_+$/g, '')
  return cleaned.length > 0 ? cleaned : 'unknown'
}

// Module-scope refs captured once at start (timers fire outside Vue/inject context).
let db: Firestore | null = null
let usersStore: ReturnType<typeof useUsersStore> | null = null
let tenantsStore: ReturnType<typeof useTenantsStore> | null = null
let projectsStore: ReturnType<typeof useProjectsStore> | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') void beat()
}

async function recordActivity(
  action?: CsmAction,
  projectId?: string,
  projectMeta?: ProjectActivityMeta,
  bucket?: string,
): Promise<void> {
  if (!db || !usersStore || !tenantsStore || !projectsStore) return
  const uid = usersStore.uid
  const email = usersStore.user?.email
  if (!uid || !email) return
  const tenantId = tenantsStore.currentTenantId || ''

  const now = new Date()
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const { dayId, slot } = localParts(now, tz)

  const payload: Record<string, unknown> = {
    user_uid: uid,
    user_email: email,
    tz,
    last_seen_at: Timestamp.fromDate(now), // absolute instant (offline-safe)
    activity: { [slot]: true }, // presence buckets
  }

  // labels: ids stay the canonical keys (in `tenants`/`actions`); names/type/role are embedded
  // for direct AI reading, captured at write time (current as-of-day; a rename/upgrade shows next day).
  const labels: Record<string, unknown> = {}
  const tenant = tenantsStore.currentTenant

  if (tenantId) {
    payload.tenants = { [tenantId]: true } // presence: set of tenants active today
    if (tenant && tenant.id === tenantId) {
      labels.tenants = {
        [tenantId]: {
          name: tenant.name,
          plan: tenant.plan,
          role: tenant.admins.includes(uid) ? 'admin' : 'member', // this user's role in this tenant
        },
      }
    }
  }
  if (action && tenantId) {
    // Route to actions[tenant][project|_tenant][action] = increment(1).
    // Firestore deep-merges nested maps, so one merge-write create-or-updates the leaf.
    const scopeKey = projectId ?? '_tenant'
    // A bucketed action counts per discriminator; every other action keeps the bare
    // counter it has always had (see BUCKETED_ACTIONS — the shape is permanent).
    const leaf = bucket && BUCKETED_ACTIONS.has(action)
      ? { [sanitizeBucket(bucket)]: increment(1) }
      : increment(1)
    payload.actions = { [tenantId]: { [scopeKey]: { [action]: leaf } } }
    if (projectId) {
      const p = projectsStore.projects?.find((pr) => pr.id === projectId)
      if (p) {
        const projectLabel: Record<string, unknown> = {
          name: p.name,
          type: p.type ?? null,
          role: p.admins.includes(uid) ? 'admin' : 'member', // this user's role in this project
        }
        if (p.type === 'td' && p.td_regulation) projectLabel.td_regulation = p.td_regulation
        if (projectMeta?.norm_count !== undefined) projectLabel.norm_count = projectMeta.norm_count
        if (projectMeta?.directive_count !== undefined) projectLabel.directive_count = projectMeta.directive_count
        const actionMeta: Record<string, unknown> = {}
        for (const k of ACTION_META_KEYS) {
          if (projectMeta?.[k] !== undefined) actionMeta[k] = projectMeta[k]
        }
        if (Object.keys(actionMeta).length > 0) projectLabel.action_meta = actionMeta
        labels.projects = { [projectId]: projectLabel }
      }
    }
  }
  if (Object.keys(labels).length > 0) payload.labels = labels

  await setDoc(doc(db, `users/${uid}/activity_sessions/${dayId}`), payload, { merge: true })
}

function beat(): Promise<void> {
  if (document.visibilityState !== 'visible') return Promise.resolve()
  return recordActivity().catch((e) => logger.debug('activity heartbeat failed', e))
}

/** Start tracking. MUST be called from a valid app context (store action / setup). Idempotent. */
export function startActivityTracker(): void {
  if (heartbeatTimer) return // already running
  try {
    db = useFirestore()
    usersStore = useUsersStore()
    tenantsStore = useTenantsStore()
    projectsStore = useProjectsStore()
  } catch (e) {
    logger.warn('activity tracker: could not resolve context', e)
    return
  }
  void beat() // immediate first bucket
  heartbeatTimer = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS)
  document.addEventListener('visibilitychange', onVisibilityChange)
}

/** Stop tracking and clear captured refs. Idempotent. */
export function stopActivityTracker(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
  document.removeEventListener('visibilitychange', onVisibilityChange)
  db = null
  usersStore = null
  tenantsStore = null
  projectsStore = null
}

/**
 * Record a curated action (also flags presence + last_seen_at). No-op if tracker not started.
 * Pass `projectId` for project-scoped actions; omit it for tenant-scoped actions.
 */
export function trackAction(
  action: CsmAction,
  opts: { projectId?: string; projectMeta?: ProjectActivityMeta; bucket?: string } = {},
): void {
  if (!db) return
  void recordActivity(action, opts.projectId, opts.projectMeta, opts.bucket).catch((e) =>
    logger.debug('trackAction failed', e),
  )
}

/** Test seam — `recordActivity` is module-private by design. */
export const __recordActivityForTest = recordActivity
